import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useClientDesignationsViewModel } from "./ClientDesignationViewModel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { StyledCardContent } from "./ClientDesignationStyles";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import AddClientDesignation from "../AddClientDesignation/AddClientDesignation";

const ClientDesignations = () => {
  const {
    clientDesignations,
    loading,
    error,
    handleDelete,
    createClientDesignation,
    updateClientDesignation,
    refetch,
    handleSearch,
  } = useClientDesignationsViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddClientDesignationOpen, setIsAddClientDesignationOpen] =
    useState(false);
  const [selectedClientDesignation, setSelectedClientDesignation] =
    useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const columns = [
    {
      id: "client_designation_name",
      label: "Name",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
    },

    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };
  const handleAddClientDesignation = () => {
    setSelectedClientDesignation(null);
    setIsAddClientDesignationOpen(true);
  };

  const handleEditClientDesignation = (ClientDesignation) => {
    setSelectedClientDesignation(ClientDesignation);
    setIsAddClientDesignationOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddClientDesignationOpen(false);
    setSelectedClientDesignation(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedClientDesignation) {
        await updateClientDesignation(
          selectedClientDesignation.client_designation_id,
          formData
        );
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Designation updated successfully",
        });
      } else {
        await createClientDesignation(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Designation added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (ClientDesignation) => {
    setDeleteConfirmation({
      ClientDesignation,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const ClientDesignation = deleteConfirmation.ClientDesignation;
    try {
      showLoading();
      await handleDelete(ClientDesignation);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Client Designation deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete client designation",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (ClientDesignation) => {
    const baseActions = [
      {
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClientDesignation,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      },
      {
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      },
    ];

    return baseActions;
  };

  if (error) {
    return (
      <StyledCardContent>
        <Typography color="error">Error: {error}</Typography>
      </StyledCardContent>
    );
  }
  const columnFilters = [
    {
      id: "client_designation_name",
      type: "select",
      label: "Client Designation Name",
      icon: <BusinessIcon />,
      sendIcon: true,
    },
  ];

  return (
    <StyledCardContent>
      <CustomTable
        columns={columns}
        data={clientDesignations}
        onAdd={handleAddClientDesignation}
        showActions={true}
        showAddButton={true}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="Client Designations"
        subtitle="Manage and monitor all client Designations"
        addButtonText="Add Client Designation"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddClientDesignation
        open={isAddClientDesignationOpen}
        onClose={handleCloseDialog}
        initialData={selectedClientDesignation}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Client Designation"
          message={`Are you sure you want to delete client designation "${deleteConfirmation.ClientDesignation.client_designation_id}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </StyledCardContent>
  );
};

export default ClientDesignations;
